import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Autocomplete, Box, Button, CircularProgress, LinearProgress, TextField } from '@mui/material'
import { mentorReportApi, MrRatingGroup, MrStudent, studentName } from '../../../features/curator/mentorReport'
import { HeadFiltersState } from '../../head/headData'
import { ui } from '../../../utils/uiTokens'
import { card, chip, Hint, inputSx, mentorNames, primaryButton, useGroupTitle } from './shared'

// Недельный вывод ментора по рейтингу группы: отстающие (из состава), что сделано, план
const RatingReportView: React.FC<{ f: HeadFiltersState; mentorId?: number }> = ({ f, mentorId }) => {
    const month = f.monthNumber || 0
    const week = f.weekNumber || 0
    const { data = [], isFetching } = mentorReportApi.useFetchRatingWeekQuery(
        { courseId: f.courseId, groupId: f.groupId, monthNumber: month, weekNumber: week, mentorId },
        { skip: !month || !week },
    )

    if (!month || !week) return <Hint id="mrPickWeek" />

    return (
        <Box>
            {isFetching && <LinearProgress sx={{ marginBottom: '12px' }} />}
            {!isFetching && !data.length && <Hint id="mrNoGroups" />}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                {data.map((g) => (
                    <RatingCard
                        key={`${g.group.groupId}:${g.group.courseSubjectId}:${g.report.updatedAt ?? ''}`}
                        group={g}
                        month={month}
                        week={week}
                    />
                ))}
            </Box>
        </Box>
    )
}

const RatingCard: React.FC<{ group: MrRatingGroup; month: number; week: number }> = ({ group, month, week }) => {
    const { formatMessage } = useIntl()
    const groupTitle = useGroupTitle()
    const ro = !group.group.canEdit
    const byId = new Map(group.students.map((s) => [s.userSubjectEnrollId, s]))
    const [lagging, setLagging] = useState<MrStudent[]>(
        group.report.laggingIds.map((id) => byId.get(id)).filter(Boolean) as MrStudent[],
    )
    const [actions, setActions] = useState(group.report.actions ?? '')
    const [plan, setPlan] = useState(group.report.plan ?? '')
    const [save, { isLoading }] = mentorReportApi.useSaveRatingMutation()

    const handleSave = async () => {
        try {
            await save({
                groupId: group.group.groupId,
                courseSubjectId: group.group.courseSubjectId,
                monthNumber: month,
                weekNumber: week,
                laggingIds: lagging.map((s) => s.userSubjectEnrollId),
                actions,
                plan,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (e) {}
    }

    return (
        <Box sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                <Box sx={{ fontWeight: 700, fontSize: '15px', color: ui.ink }}>{groupTitle(group.group)}</Box>
                <Box sx={chip(group.report.filled ? 'ok' : 'neutral')}>
                    {formatMessage({ id: group.report.filled ? 'mrFilled' : 'mrNotFilled' })}
                </Box>
            </Box>
            <Box sx={{ color: ui.muted, fontSize: '12.5px', marginBottom: '14px' }}>
                {[mentorNames(group.group), group.report.updatedAt?.replace('T', ' ')].filter(Boolean).join(' · ')}
            </Box>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Autocomplete
                    multiple
                    size="small"
                    disabled={ro}
                    options={group.students}
                    value={lagging}
                    onChange={(_e, v) => setLagging(v as MrStudent[])}
                    getOptionLabel={studentName}
                    isOptionEqualToValue={(a, b) => a.userSubjectEnrollId === b.userSubjectEnrollId}
                    noOptionsText={formatMessage({ id: 'mrNoStudents' })}
                    renderInput={(params) => (
                        <TextField {...params} label={formatMessage({ id: 'mrLagging' })} sx={inputSx} />
                    )}
                    ChipProps={{ size: 'small', sx: { backgroundColor: ui.pinkSoft, color: ui.pinkDark } }}
                />
                <TextField size="small" multiline minRows={2} label={formatMessage({ id: 'mrActions' })}
                    placeholder={formatMessage({ id: 'mrActionsHint' })} value={actions}
                    onChange={(e) => setActions(e.target.value)} disabled={ro} sx={inputSx} />
                <TextField size="small" multiline minRows={2} label={formatMessage({ id: 'mrPlan' })} value={plan}
                    onChange={(e) => setPlan(e.target.value)} disabled={ro} sx={inputSx} />
            </Box>
            {!ro && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                    <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={primaryButton}>
                        {isLoading ? <CircularProgress size={18} sx={{ color: ui.card }} /> : formatMessage({ id: 'save' })}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export default RatingReportView
